// BookEasy engine — pure logic. Self-hosted booking schedule.

export interface Service {
  id: string;
  name: string;
  durationMin: number;
  bufferMin: number;
  priceCents: number;
  description: string;
}

export interface BusinessHours {
  // 0=Sun, 1=Mon, ... 6=Sat
  [day: number]: { open: string; close: string; closed?: boolean }; // HH:mm
}

export interface Booking {
  id: string;
  serviceId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  startISO: string;
  endISO: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  notes: string;
  createdISO: string;
}

export interface BusinessProfile {
  name: string;
  email: string;
  phone: string;
  timezone: string;
  slotIntervalMin: number;
  hours: BusinessHours;
}

export function defaultHours(): BusinessHours {
  return {
    0: { open: '10:00', close: '15:00', closed: true },
    1: { open: '09:00', close: '17:00' },
    2: { open: '09:00', close: '17:00' },
    3: { open: '09:00', close: '17:00' },
    4: { open: '09:00', close: '17:00' },
    5: { open: '09:00', close: '17:00' },
    6: { open: '10:00', close: '14:00', closed: true },
  };
}

export function defaultProfile(): BusinessProfile {
  return {
    name: 'My Business',
    email: '',
    phone: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    slotIntervalMin: 30,
    hours: defaultHours(),
  };
}

export function generateSlots(date: Date, service: Service, profile: BusinessProfile, existing: Booking[]): { iso: string; label: string }[] {
  const day = date.getDay();
  const h = profile.hours[day];
  if (!h || h.closed) return [];
  const [openH, openM] = h.open.split(':').map(Number);
  const [closeH, closeM] = h.close.split(':').map(Number);
  const slotMs = profile.slotIntervalMin * 60_000;
  const serviceTotalMs = (service.durationMin + service.bufferMin) * 60_000;

  const dayStart = new Date(date); dayStart.setHours(openH, openM, 0, 0);
  const dayEnd = new Date(date); dayEnd.setHours(closeH, closeM, 0, 0);

  const slots: { iso: string; label: string }[] = [];
  for (let t = dayStart.getTime(); t + serviceTotalMs <= dayEnd.getTime(); t += slotMs) {
    const slotStart = new Date(t);
    const slotEnd = new Date(t + serviceTotalMs);
    const overlaps = existing.some(b => {
      if (b.status === 'cancelled') return false;
      const bs = new Date(b.startISO).getTime();
      const be = new Date(b.endISO).getTime();
      return slotStart.getTime() < be && bs < slotEnd.getTime();
    });
    if (!overlaps && slotStart.getTime() > Date.now()) {
      slots.push({ iso: slotStart.toISOString(), label: slotStart.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) });
    }
  }
  return slots;
}

export function newBooking(service: Service, startISO: string): Booking {
  const start = new Date(startISO);
  const end = new Date(start.getTime() + service.durationMin * 60_000);
  return {
    id: crypto.randomUUID(),
    serviceId: service.id,
    customerName: '', customerEmail: '', customerPhone: '',
    startISO, endISO: end.toISOString(),
    status: 'pending', notes: '', createdISO: new Date().toISOString(),
  };
}

export function generateICS(b: Booking, s: Service, profile: BusinessProfile): string {
  const dt = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//CatalystAgile//BookEasy//EN
BEGIN:VEVENT
UID:${b.id}@catalystagile
DTSTAMP:${dt(b.createdISO)}
DTSTART:${dt(b.startISO)}
DTEND:${dt(b.endISO)}
SUMMARY:${s.name} — ${b.customerName}
DESCRIPTION:Service: ${s.name}\\nCustomer: ${b.customerName}\\nEmail: ${b.customerEmail}\\nPhone: ${b.customerPhone}\\nNotes: ${b.notes}
ORGANIZER:mailto:${profile.email}
ATTENDEE:mailto:${b.customerEmail}
STATUS:${b.status === 'confirmed' ? 'CONFIRMED' : 'TENTATIVE'}
END:VEVENT
END:VCALENDAR`;
}

export function shareableLink(profile: BusinessProfile): string {
  const slug = profile.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `https://yourdomain.com/book/${slug}`;
}

export function exportBookingsCSV(bookings: Booking[], services: Service[]): string {
  const headers = ['Date','Time','Service','Customer','Email','Phone','Status','Notes'];
  const rows = bookings.map(b => {
    const s = services.find(x => x.id === b.serviceId);
    const d = new Date(b.startISO);
    return [d.toLocaleDateString(), d.toLocaleTimeString(), s?.name || '', b.customerName, b.customerEmail, b.customerPhone, b.status, b.notes.replace(/\n/g, ' ')];
  });
  return [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
}
